const _ = require("lodash");
const {
	DEFAULT_ERROR_LIMIT,
	LOW_PRIORITY_ERRORS,
	REQUIRE_REPEAT_ERRORS,
} = require("./Constants");

/**
 * A error logger and storage mechanism. The primary use of this file is to
 * have a generic ErrorUtil.log file that is wired up at initialization.
 *
 * While we were originally moving to Sentry, ErrorUtil was intentionally
 * set up to be agnostic to how the stored errors are handled. So we still
 * mostly use it as-is, and code with ErrorUtil.log needs no migration.
 */

let errorLimit = DEFAULT_ERROR_LIMIT;
let loggedErrors = [];
let listeners = [];
let diagnosticsFunction = () => null;

const addListener = function(cb) {
	typeof cb === "function" && listeners.push(cb);
};

const addListenerAndCallForPrevious = function(cb) {
	addListener(cb);
	loggedErrors.forEach(errorObj => cb(errorObj));
};

const removeListener = function(cb) {
	listeners = listeners.filter(x => x !== cb);
};

const getLoggedErrors = function() {
	return loggedErrors;
};

const getCallStack = function(errorObj) {
	let stack = "<unknown>";
	if (errorObj && typeof errorObj.stack === "string") {
		stack = errorObj.stack.replace(/\s*\n\s*/g, " | ");
	}
	return stack;
};

const lastErrorByType = {};

// Returns whether an error of this type should be allowed to go through due to
// rules about requiring repeats. Errors not in the REQUIRE_REPEAT_ERRORS table
// always return true. Also modifies internal repeat-tracking state.
const checkRepeatCondition = function(type) {
	const delay = REQUIRE_REPEAT_ERRORS[type];
	if (!_.isNumber(delay)) {
		// No repeat requirement.
		return true;
	}
	const last = lastErrorByType[type];
	const now = Date.now();
	lastErrorByType[type] = now;
	if (!_.isFinite(last)) {
		return false;
	} else if (delay === 0) {
		// delay 0 means any repeat counts.
		return true;
	} else if (last + delay >= now) {
		return true;
	}
	return false;
};

// Internal helper
const storeError = function(type, message, data) {
	if (loggedErrors.length >= errorLimit) return;
	// Skip logging low priority errors
	if (_.includes(LOW_PRIORITY_ERRORS, type)) return;
	const errorObj = {
		type,
		message,
		data,
		diagnostics: diagnosticsFunction(),
	};
	loggedErrors.push(errorObj);
	listeners.forEach(cb => cb(errorObj));
};

// Takes in the error message and checks if it should not be logged to the
// frontend error logs. All checks here should have comments explaining why
// those errors don't need to be logged.
const shouldSkipLogging = function(message, url, stack, data) {
	if (/^chrome-extension:\/\//.test(url)) {
		return true;
	} else if (/^data:application\/javascript;base64/.test(url)) {
		// Bad javascript that came from a data URL, which we're pretty sure is not
		// ours. Exact source not known. See T10365 for the logged error.
		return true;
	} else if (/^Script error/i.test(message)) {
		// This error originated from an outside source (browser extension, etc.).
		// See the following for more information:
		// http://stackoverflow.com/questions/5913978/cryptic-script-error-reported-in-javascript-in-chrome-and-firefox
		return true;
	} else if (
		/undefined is not an object \(evaluating '__gCrWeb\.autofill\.extractForms'\)$/.test(
			message
		)
	) {
		// These are errors from using autofill on iOS Chrome that get thrown in
		// large numbers. See the following for more info:
		// https://bugs.chromium.org/p/chromium/issues/detail?id=590375
		// We can remove this catch when the issue gets resolved.
		return true;
	} else if (
		/www\.paypalobjects\.com\/api\/checkout\.js/.test(url) ||
		/https:\/\/www\.paypalobjects\.com\/api\/checkout\.js/.test(stack) ||
		/https:\/\/www\.paypalobjects\.com\/api\/checkout\.js/.test(
			_.get(data, ["otherArgs", 0, "error"], "")
		) ||
		/^Blocked a frame with origin "https:\/\/beastacademy\.com" from accessing a frame with origin "https:\/\/www\.paypal\.com"\. Protocols, domains, and ports must match\.$/.test(
			message
		) ||
		/^Error flushing:$/.test(message)
	) {
		// Ignoring all errors from paypal code as well as one about loading paypal
		// and it being blocked.
		return true;
	} else if (/\/autotrack\.js$/.test(url)) {
		//Ignore errors from autotrack.js
		return true;
	} else if (
		/^SecurityError: DOM Exception 18: An attempt was made to break through the security policy of the user agent\.$/.test(
			message
		) ||
		/^Attempt to use history\.(pushState|replaceState)\(\) more than 100 times per 30(?:\.000000)? seconds$/.test(
			message
		)
	) {
		// Exact text of Safari's error when pushState is used 100 times. See
		// T7315. We can ignore this as it at worst seems to block page changes for
		// a short time.
		return true;
	} else if (
		/moz-extension:\/\//.test(stack) ||
		/chrome-extension:\/\//.test(stack)
	) {
		// Exception came from a browser extension
		return true;
	} else if (
		/^Uncaught ReferenceError: (GiveasyouLive|\$) is not defined$/.test(
			message
		) ||
		/^Can't execute code from a freed script$/.test(message) ||
		/^fillForm@/.test(stack) ||
		/^Cannot read property 'feedConf' of null$/.test(message) ||
		/^Uncaught TypeError: a\[b\]\.target\.className\.indexOf is not a function$/.test(
			message
		) ||
		/\.data\.indexOf is not a function$/.test(message) ||
		/__firefox__/.test(message) ||
		/^document\.getElementsByClassName\.ToString is not a function$/.test(
			message
		) ||
		/a\[b\]\.target\.className\.indexOf is not a function/.test(message) ||
		/navigator\.sendBeacon is not a function/.test(message) ||
		/evaluating 'window\.sogouMobileExplorer\.triggerEvent'/.test(message) ||
		/^InvalidStateError: The object is in an invalid state\.$/.test(message) ||
		/document\.head\.querySelector\('meta\[name='supported-color-schemes'\]'\)/.test(
			message
		) ||
		/document\.getElementsByTagName\('video'\)\[0\]\.webkitExitFullScreen/.test(
			message
		) ||
		/ResizeObserver/.test(message) ||
		/from accessing a cross-origin frame\. Protocols, domains, and ports must match\./.test(
			message
		) ||
		/Unexpected token </.test(message) ||
		/to webkitAudioContext\.decodeAudioData must be an instance of ArrayBuffer/.test(
			message
		)
	) {
		//Ignore these other common errors
		return true;
	}
	return false;
};

// message should be a string. data can be anything JSON-serializable.
const log = function(type, message, data) {
	// TODO: determine if we can remove all this
	if (loggedErrors.length >= errorLimit) {
		return;
	}
	const repeatOk = checkRepeatCondition(type);
	if (!repeatOk) {
		return;
	}
	if (Array.isArray(data)) {
		data = {errorList: data}; // _.extend calls below wipe array structure out.
	} else if (data && !_.isObject(data)) {
		data = {data};
	}
	// Allow passing an exception for the second argument.
	if (message instanceof Error) {
		data = _.extend({}, data, {stack: getCallStack(message)});
		message = message.message;
	} else if (data instanceof Error) {
		data = {
			error: String(data),
			stack: getCallStack(data),
		};
	} else {
		data = _.extend({}, data, {stack: getCallStack(new Error())});
	}
	if (shouldSkipLogging(message, "", data.stack, data)) {
		return;
	} else {
		storeError(type, message, data);
	}
};

// Used in tests only. Just clears the list of logged errors.
const resetLogged = function() {
	loggedErrors = [];
};

// Used in tests only (although not yet). Changes the limit on the number of
// logged errors. Pass Infinity to disable the limit.
const setErrorLimit = function(newLimit) {
	errorLimit = newLimit;
};

// Set a function taking no arguments which returns info about the current
// state of the user's session.
const setDiagnosticsFunction = function(f) {
	diagnosticsFunction = f;
};

const onWindowError = function(message, url, line, col, errorObj) {
	if (loggedErrors.length >= errorLimit) return;
	if ((message && typeof message === "object") || col === undefined) {
		line = message.lineno || line || 0;
		col = message.colno || col || 0;
		url = message.filename || "<unknown>";
		if (message.error && message.error.message) {
			errorObj = message.error;
			message = message.error.message;
		} else if (message.message) {
			errorObj = message;
			message = message.message;
		} else {
			message = "" + message;
		}
	}
	const stack = getCallStack(errorObj);
	if (shouldSkipLogging(message, url, stack)) {
		return;
	} else {
		storeError("E_WINDOW_ERROR", message, {url, line, col, stack});
	}
};

let trapperIsSetUp = false;
const setupExceptionTrapper = function() {
	if (trapperIsSetUp || !global.window) {
		return;
	}
	trapperIsSetUp = true;

	global.window.addEventListener("error", onWindowError);
};

module.exports = {
	addListener,
	addListenerAndCallForPrevious,
	removeListener,
	getLoggedErrors,
	log,
	resetLogged,
	shouldSkipLogging,
	setErrorLimit,
	setDiagnosticsFunction,
	setupExceptionTrapper,
};
